import React, { useCallback, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WAWidgetSizeToWeatherSize } from "../../WidgetAdd/utils/WAWidgetSizeToWeatherSize";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";

import { WAWeatherMutation } from "./__generated__/WAWeatherMutation.graphql";
import { WAWeatherSettings } from "./WAWeatherSettings";
import { WAWeatherPreview } from "./WAWeatherPreview";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { weatherWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/weatherWidgetSizes";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetScreen } from "../../common/WidgetScreen";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetAddWeather">;
  route: UserLoggedInNavigatorRouteProp<"WidgetAddWeather">;
};

export const WAWeather = withWebModal(WAWeatherC, true);

function WAWeatherC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWAWeather {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAWeather(props: Props & { fetchKey: string }) {
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const [location, setLocation] = useState<
    | {
        lat: number;
        lng: number;
        desc: string;
        type: "latlng";
      }
    | {
        placeId: string;
        desc: string;
        type: "place";
      }
    | null
  >(null);

  const [createWidgetAddWidgetWeather, loading] =
    useMutation<WAWeatherMutation>(graphql`
      mutation WAWeatherMutation(
        $storecastId: ID!
        $input: CreateWidgetWeatherInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetWeather(widget: $input) {
            id
          }
        }
      }
    `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidgetAddWidgetWeather({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            lat: location?.type === "latlng" ? location.lat : undefined,
            lng: location?.type === "latlng" ? location.lng : undefined,
            placeId: location?.type === "place" ? location.placeId : undefined,
            title: location?.desc,
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToWeatherSize(size),
          },
        },
        onCompleted: () => {
          props.navigation.pop(2);
        },
      });
    },
    [
      createWidgetAddWidgetWeather,
      location,
      props.navigation,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
    ]
  );

  const intl_tokens = useIntlTokens();

  return (
    <WidgetScreen
      preview={
        <WASizePreview
          disabled={location === null}
          loading={loading}
          onCreate={onCreate}
          maxSize={props.route.params.maxSize}
          unitSize={props.route.params.unitSize}
          widgetSizes={weatherWidgetSizes}
        >
          {location
            ? (size) => (
                <WAWeatherPreview
                  location={
                    location.type === "latlng" ? location : location.placeId
                  }
                  size={size}
                  storecastId={props.route.params.storecastId}
                />
              )
            : null}
        </WASizePreview>
      }
      settings={
        <>
          {!isCompact ? (
            <WAFakeHeader title={intl_tokens.SelectLocation} />
          ) : null}
          <WAWeatherSettings location={location} setLocation={setLocation} />
        </>
      }
    />
  );
}
